import React, { useMemo, useState } from 'react'
import useStore from '../hooks/useStore'
import { computeUnstoppableStreak } from '../utils/streakUtils'

const PDF_PAGE_WIDTH = 595
const PDF_PAGE_HEIGHT = 842
const PDF_LEFT = 46
const PDF_RIGHT = 46
const PDF_TOP = 54
const PDF_BOTTOM = 54

function escapePdfText(text) {
  return String(text)
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)')
}

function wrapPdfText(text, maxWidth, fontSize) {
  const normalized = String(text || '')
    .replace(/\s+/g, ' ')
    .trim()

  if (!normalized) return ['-']

  const approxCharWidth = fontSize * 0.52
  const maxChars = Math.max(12, Math.floor(maxWidth / approxCharWidth))
  const words = normalized.split(' ')
  const lines = []
  let currentLine = ''

  words.forEach((word) => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word
    if (nextLine.length <= maxChars) {
      currentLine = nextLine
      return
    }

    if (currentLine) {
      lines.push(currentLine)
    }

    if (word.length <= maxChars) {
      currentLine = word
      return
    }

    let remaining = word
    while (remaining.length > maxChars) {
      lines.push(remaining.slice(0, maxChars - 1) + '-')
      remaining = remaining.slice(maxChars - 1)
    }
    currentLine = remaining
  })

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function createPdfBlob(pageStreams) {
  const encoder = new TextEncoder()
  const objects = []

  objects[1] = '<< /Type /Catalog /Pages 2 0 R >>'
  objects[2] = ''
  objects[3] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>'
  objects[4] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>'
  objects[5] = '<< /Type /Font /Subtype /Type1 /BaseFont /Courier >>'

  const pageIds = []

  pageStreams.forEach((stream) => {
    const contentId = objects.length
    const streamBytes = encoder.encode(stream).length
    objects.push(`<< /Length ${streamBytes} >>\nstream\n${stream}\nendstream`)

    const pageId = objects.length
    pageIds.push(pageId)
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PDF_PAGE_WIDTH} ${PDF_PAGE_HEIGHT}] /Resources << /Font << /F1 3 0 R /F2 4 0 R /F3 5 0 R >> >> /Contents ${contentId} 0 R >>`
    )
  })

  objects[2] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`

  let pdf = '%PDF-1.4\n'
  const offsets = [0]

  for (let id = 1; id < objects.length; id += 1) {
    offsets[id] = encoder.encode(pdf).length
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`
  }

  const xrefStart = encoder.encode(pdf).length
  pdf += `xref\n0 ${objects.length}\n`
  pdf += '0000000000 65535 f \n'

  for (let id = 1; id < objects.length; id += 1) {
    pdf += `${String(offsets[id]).padStart(10, '0')} 00000 n \n`
  }

  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`

  return new Blob([encoder.encode(pdf)], { type: 'application/pdf' })
}

function buildMonthlyReportPdf({
  month,
  entries,
  monthlyAverage,
  monthlyBest,
  monthlyWeakest,
  monthlyGoal
}) {
  const pages = []
  let commands = []
  let cursorY = PDF_PAGE_HEIGHT - PDF_TOP

  const addCommand = (command) => {
    commands.push(command)
  }

  const fillRect = (x, y, width, height, color) => {
    addCommand(`${color.join(' ')} rg ${x} ${y} ${width} ${height} re f`)
  }

  const strokeLine = (x1, y1, x2, y2, color, width = 1) => {
    addCommand(`${width} w ${color.join(' ')} RG ${x1} ${y1} m ${x2} ${y2} l S`)
  }

  const addText = (text, x, y, size = 12, font = 'F1', color = [0.96, 0.98, 1]) => {
    addCommand(`BT /${font} ${size} Tf ${color.join(' ')} rg 1 0 0 1 ${x} ${y} Tm (${escapePdfText(text)}) Tj ET`)
  }

  const startPage = () => {
    commands = []
    cursorY = PDF_PAGE_HEIGHT - PDF_TOP

    fillRect(PDF_LEFT, cursorY - 22, 28, 28, [0.89, 0.67, 0.16])
    addText('R', PDF_LEFT + 8.5, cursorY - 13, 15, 'F2', [0.07, 0.07, 0.07])
    addText('ResultOS', PDF_LEFT + 38, cursorY - 10, 24, 'F2', [0.97, 0.98, 1])
    addText('MONTHLY PERFORMANCE REPORT', PDF_LEFT + 38, cursorY - 29, 10, 'F3', [0.52, 0.57, 0.63])
    addText(`${month.monthLabel} ${month.year}`, PDF_PAGE_WIDTH - 170, cursorY - 14, 14, 'F2', [0.89, 0.67, 0.16])
    strokeLine(PDF_LEFT, cursorY - 42, PDF_PAGE_WIDTH - PDF_RIGHT, cursorY - 42, [0.22, 0.25, 0.28])
    cursorY -= 68
  }

  const finishPage = () => {
    pages.push(commands.join('\n'))
  }

  const ensureSpace = (heightNeeded) => {
    if (cursorY - heightNeeded < PDF_BOTTOM) {
      finishPage()
      startPage()
    }
  }

  const addSectionTitle = (title) => {
    ensureSpace(24)
    addText(title, PDF_LEFT, cursorY, 11, 'F3', [0.52, 0.57, 0.63])
    cursorY -= 12
    strokeLine(PDF_LEFT, cursorY, PDF_PAGE_WIDTH - PDF_RIGHT, cursorY, [0.18, 0.21, 0.24])
    cursorY -= 18
  }

  const addWrappedParagraph = (text, options = {}) => {
    const {
      x = PDF_LEFT,
      maxWidth = PDF_PAGE_WIDTH - PDF_LEFT - PDF_RIGHT,
      size = 12,
      leading = size + 4,
      font = 'F1',
      color = [0.88, 0.91, 0.96]
    } = options

    const lines = wrapPdfText(text, maxWidth, size)
    ensureSpace(lines.length * leading + 4)
    lines.forEach((line) => {
      addText(line, x, cursorY, size, font, color)
      cursorY -= leading
    })
  }

  const addLabelValue = (label, value) => {
    addWrappedParagraph(label, {
      size: 10,
      leading: 13,
      font: 'F3',
      color: [0.52, 0.57, 0.63]
    })
    cursorY += 1
    addWrappedParagraph(value, {
      size: 13,
      leading: 17,
      font: 'F1',
      color: [0.96, 0.98, 1]
    })
    cursorY -= 2
  }

  startPage()

  addSectionTitle('MONTH SUMMARY')
  addLabelValue('Tracked Days', `${entries.length}`)
  addLabelValue('Average R Score', `${monthlyAverage}`)
  addLabelValue('Weakest Variable', monthlyWeakest)
  addLabelValue(
    'Best Day',
    monthlyBest
      ? `${monthlyBest.date} - R ${monthlyBest.r_score} - Grade ${monthlyBest.grade || 'N/A'}`
      : '-'
  )

  if (monthlyGoal) {
    addLabelValue('Monthly Result Area', monthlyGoal)
  }

  cursorY -= 8
  addSectionTitle('ALL ENTRIES')

  entries.forEach((entry, index) => {
    const goalLines = wrapPdfText(entry.goal || '-', PDF_PAGE_WIDTH - PDF_LEFT - PDF_RIGHT - 20, 12)
    const reflectionLines = wrapPdfText(entry.reflection || 'No reflection recorded.', PDF_PAGE_WIDTH - PDF_LEFT - PDF_RIGHT - 20, 11)
    const blockHeight = 54 + (goalLines.length * 16) + (reflectionLines.length * 15)
    ensureSpace(blockHeight)

    fillRect(PDF_LEFT, cursorY - 10, PDF_PAGE_WIDTH - PDF_LEFT - PDF_RIGHT, 24, [0.12, 0.16, 0.18])
    addText(
      `${entry.date}   R ${entry.r_score}   Grade ${entry.grade || 'N/A'}`,
      PDF_LEFT + 10,
      cursorY - 2,
      12,
      'F2',
      [0.97, 0.98, 1]
    )
    cursorY -= 30

    addWrappedParagraph(`Winning Result: ${entry.goal || '-'}`, {
      x: PDF_LEFT + 10,
      maxWidth: PDF_PAGE_WIDTH - PDF_LEFT - PDF_RIGHT - 20,
      size: 12,
      leading: 16,
      font: 'F1',
      color: [0.96, 0.98, 1]
    })

    addWrappedParagraph(
      `W ${entry.w_score} x C ${entry.c_score} + T ${entry.t_score} = R ${entry.r_score}`,
      {
        x: PDF_LEFT + 10,
        maxWidth: PDF_PAGE_WIDTH - PDF_LEFT - PDF_RIGHT - 20,
        size: 11,
        leading: 15,
        font: 'F3',
        color: [0.49, 0.8, 0.63]
      }
    )

    addWrappedParagraph(`Reflection: ${entry.reflection || 'No reflection recorded.'}`, {
      x: PDF_LEFT + 10,
      maxWidth: PDF_PAGE_WIDTH - PDF_LEFT - PDF_RIGHT - 20,
      size: 11,
      leading: 15,
      font: 'F1',
      color: [0.8, 0.84, 0.89]
    })

    cursorY -= 6
    strokeLine(PDF_LEFT, cursorY, PDF_PAGE_WIDTH - PDF_RIGHT, cursorY, [0.18, 0.21, 0.24])
    cursorY -= index === entries.length - 1 ? 0 : 18
  })

  finishPage()
  return createPdfBlob(pages)
}

function HistoryPanel() {
  const {
    history,
    loadHistory,
    showToast,
    getMonthlyGoalForSelection
  } = useStore()
  const [reportOpen, setReportOpen] = useState(false)
  const { currentStreak, longestStreak, totalShipped } = computeUnstoppableStreak(history)

  const handleRefresh = async () => {
    try {
      await loadHistory()
      showToast('✓', 'History refreshed')
    } catch (error) {
      showToast('❌', 'Failed to refresh history')
    }
  }

  const getGradeColor = (score) => {
    if (score >= 100) return 'var(--gold2)'
    if (score >= 80) return 'var(--sage2)'
    if (score >= 60) return 'var(--azure2)'
    if (score >= 40) return 'var(--gold)'
    return 'var(--coral2)'
  }

  const getGrade = (score) => {
    if (score >= 100) return 'S'
    if (score >= 80) return 'A'
    if (score >= 60) return 'B'
    if (score >= 40) return 'C'
    return 'D'
  }

  const daysTracked = history.length
  const avgScore = history.length > 0
    ? Math.round(history.reduce((sum, h) => sum + h.r_score, 0) / history.length)
    : 0
  const bestDay = history.length > 0
    ? Math.max(...history.map((h) => h.r_score))
    : 0

  const avgW = history.length > 0 ? history.reduce((sum, h) => sum + h.w_score, 0) / history.length : 0
  const avgC = history.length > 0 ? history.reduce((sum, h) => sum + h.c_score, 0) / history.length : 0
  const avgT = history.length > 0 ? history.reduce((sum, h) => sum + h.t_score, 0) / history.length : 0

  let weakest = '-'
  if (history.length > 0) {
    const minAvg = Math.min(avgW, avgC, avgT)
    if (minAvg === avgW) weakest = 'W'
    else if (minAvg === avgC) weakest = 'C'
    else weakest = 'T'
  }

  const last7Days = history.slice(0, 7).reverse()

  const monthOptions = useMemo(() => {
    const monthMap = new Map()

    history.forEach((entry) => {
      const date = new Date(`${entry.date}T00:00:00`)
      if (Number.isNaN(date.getTime())) return

      const year = date.getFullYear()
      const monthNumber = date.getMonth() + 1
      const monthLabel = date.toLocaleString('en-US', { month: 'long' })
      const key = `${year}-${String(monthNumber).padStart(2, '0')}`

      if (!monthMap.has(key)) {
        monthMap.set(key, {
          key,
          year,
          monthNumber,
          monthLabel
        })
      }
    })

    const options = Array.from(monthMap.values())
    options.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year
      return b.monthNumber - a.monthNumber
    })
    return options
  }, [history])

  const [selectedMonthKey, setSelectedMonthKey] = useState(monthOptions[0]?.key || '')

  React.useEffect(() => {
    if (!monthOptions.length) {
      if (selectedMonthKey) setSelectedMonthKey('')
      return
    }

    if (!monthOptions.some((option) => option.key === selectedMonthKey)) {
      setSelectedMonthKey(monthOptions[0].key)
    }
  }, [monthOptions, selectedMonthKey])

  const selectedMonth = monthOptions.find((option) => option.key === selectedMonthKey) || null

  const monthlyEntries = useMemo(() => {
    if (!selectedMonth) return []

    return history
      .filter((entry) => {
        const date = new Date(`${entry.date}T00:00:00`)
        return (
          date.getFullYear() === selectedMonth.year &&
          date.getMonth() + 1 === selectedMonth.monthNumber
        )
      })
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((entry) => ({
        ...entry,
        grade: entry.grade || getGrade(entry.r_score)
      }))
  }, [history, selectedMonth])

  const monthlyAverage = monthlyEntries.length > 0
    ? Math.round(monthlyEntries.reduce((sum, entry) => sum + entry.r_score, 0) / monthlyEntries.length)
    : 0

  const monthlyBest = monthlyEntries.length > 0
    ? monthlyEntries.reduce((best, entry) => (entry.r_score > best.r_score ? entry : best), monthlyEntries[0])
    : null

  const monthlyWeakest = (() => {
    if (!monthlyEntries.length) return '-'

    const avgMonthW = monthlyEntries.reduce((sum, entry) => sum + entry.w_score, 0) / monthlyEntries.length
    const avgMonthC = monthlyEntries.reduce((sum, entry) => sum + entry.c_score, 0) / monthlyEntries.length
    const avgMonthT = monthlyEntries.reduce((sum, entry) => sum + entry.t_score, 0) / monthlyEntries.length
    const minAvg = Math.min(avgMonthW, avgMonthC, avgMonthT)

    if (minAvg === avgMonthW) return 'W'
    if (minAvg === avgMonthC) return 'C'
    return 'T'
  })()

  const handleDownloadReport = async () => {
    if (!selectedMonth || monthlyEntries.length === 0) {
      showToast('✕', 'No history found for that month')
      return
    }

    try {
      const monthlyGoalForReport = await getMonthlyGoalForSelection({
        monthNumber: selectedMonth.monthNumber,
        year: selectedMonth.year
      })

      const pdfBlob = buildMonthlyReportPdf({
        month: selectedMonth,
        entries: monthlyEntries,
        monthlyAverage,
        monthlyBest,
        monthlyWeakest,
        monthlyGoal: monthlyGoalForReport
      })

      const fileMonth = selectedMonth.monthLabel.toLowerCase()
      downloadBlob(pdfBlob, `resultos-report-${selectedMonth.year}-${fileMonth}.pdf`)
      showToast('✓', 'Monthly PDF downloaded')
    } catch (error) {
      console.error('Download monthly report error:', error)
      showToast('✕', 'Failed to load monthly goal for report')
    }
  }

  return (
    <div className="panel active">
      <div className="pg-ey">Review</div>
      <div className="pg-ti">Performance <em>History</em></div>
      <div className="pg-su">
        Your results over time. The trend reveals which variable you chronically underprice.
      </div>

      {/* ── Unstoppable Streak Stats ── */}
      <div className="g3 mb24">
        <div className="stat-t history-streak-card">
          <div className="st-l">Current Streak</div>
          <div className="st-v history-streak-val" style={{color:'var(--primary)'}}>
            {currentStreak} {currentStreak === 1 ? 'day' : 'days'}
          </div>
        </div>
        <div className="stat-t history-streak-card">
          <div className="st-l">Longest Streak</div>
          <div className="st-v history-streak-val" style={{color:'var(--gold2)'}}>
            {longestStreak} {longestStreak === 1 ? 'day' : 'days'}
          </div>
        </div>
        <div className="stat-t history-streak-card">
          <div className="st-l">Total Days Shipped</div>
          <div className="st-v history-streak-val" style={{color:'var(--sage2)'}}>
            {totalShipped}
          </div>
        </div>
      </div>

      <div className="g4 mb24">
        <div className="stat-t">
          <div className="st-l">Days Tracked</div>
          <div className="st-v" style={{ color: 'var(--gold)' }}>{daysTracked}</div>
        </div>
        <div className="stat-t">
          <div className="st-l">Avg R Score</div>
          <div className="st-v" style={{ color: 'var(--azure2)' }}>{avgScore || '-'}</div>
        </div>
        <div className="stat-t">
          <div className="st-l">Best Day</div>
          <div className="st-v" style={{ color: 'var(--sage2)' }}>{bestDay || '-'}</div>
        </div>
        <div className="stat-t">
          <div className="st-l">Weakest Variable</div>
          <div className="st-v" style={{ color: 'var(--coral2)', fontSize: '24px' }}>{weakest}</div>
        </div>
      </div>
      <div className="sec">7-Day Trend</div>
      <div className="card mb24">
        <div className="c-lbl">R Score by Day</div>
        <div className="chart-wrap">
          {last7Days.length === 0 ? (
            <div style={{ fontSize: '11px', color: 'var(--text3)', fontStyle: 'italic', alignSelf: 'center' }}>
              Save some days to see your chart.
            </div>
          ) : (
            last7Days.map((day, index) => {
              const pct = Math.round((day.r_score / 110) * 100)
              const color = getGradeColor(day.r_score)
              return (
                <div key={index} className="cc">
                  <div className="cv" style={{ color }}>{day.r_score}</div>
                  <div className="ct2">
                    <div
                      className="cf"
                      style={{
                        height: `${pct}%`,
                        background: `linear-gradient(180deg,${color}99,${color}33)`
                      }}
                    ></div>
                  </div>
                  <div className="cd">{day.date.slice(5)}</div>
                </div>
              )
            })
          )}
        </div>
      </div>
      <div className="sec">All Entries</div>
      <div
        style={{
          background: 'var(--bg2)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r2)',
          overflow: 'hidden'
        }}
      >
        <div className="hr hd">
          <div>Date</div>
          <div>Goal</div>
          <div>W x C + T</div>
          <div>R</div>
          <div>Grade</div>
        </div>
        {history.length === 0 ? (
          <div
            style={{
              padding: '32px',
              textAlign: 'center',
              fontSize: '12px',
              color: 'var(--text3)',
              fontStyle: 'italic'
            }}
          >
            No history yet.
          </div>
        ) : (
          history.map((entry, index) => {
            const color = getGradeColor(entry.r_score)
            return (
              <div key={index} className="hr">
                <div className="h-date">{entry.date}</div>
                <div className="h-goal">{entry.goal || '-'}</div>
                <div className="h-sc">
                  <span style={{ color: 'var(--azure2)' }}>{entry.w_score}</span> x{' '}
                  <span style={{ color: 'var(--sage2)' }}>{entry.c_score}</span> +{' '}
                  <span style={{ color: 'var(--coral2)' }}>{entry.t_score}</span>
                </div>
                <div className="h-r" style={{ color }}>
                  {entry.r_score}
                </div>
                <div className="h-g" style={{ color, background: `${color}22` }}>
                  {entry.grade || getGrade(entry.r_score)}
                </div>
              </div>
            )
          })
        )}
      </div>
      <div className="flex gap12 mt20">
        <button
          className="btn bg-ghost"
          onClick={() => setReportOpen((current) => !current)}
          style={{ color: 'var(--primary2)', borderColor: 'rgba(227,161,39,0.24)' }}
        >
          Download Report
        </button>
        <button
          className="btn bg-ghost"
          onClick={handleRefresh}
          style={{ color: 'var(--azure2)', borderColor: 'rgba(77,158,232,0.2)' }}
        >
          Refresh from Cloud
        </button>
      </div>
      {reportOpen && (
        <div className="history-mailer">
          <div className="history-mailer-head">
            <div>
              <div className="c-lbl">Download Monthly PDF</div>
              <div className="history-mailer-sub">
                Choose a saved month and download a branded ResultOS PDF with the month summary plus every daily record.
              </div>
            </div>
            <div className="history-mailer-badge">
              {monthlyEntries.length} {monthlyEntries.length === 1 ? 'entry' : 'entries'}
            </div>
          </div>
          <div className="history-mailer-grid history-mailer-grid-single">
            <div>
              <label className="a-lbl">Month</label>
              <select
                className="sel"
                value={selectedMonthKey}
                onChange={(event) => setSelectedMonthKey(event.target.value)}
                disabled={monthOptions.length === 0}
              >
                {monthOptions.length === 0 ? (
                  <option value="">No saved months yet</option>
                ) : (
                  monthOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.monthLabel} {option.year}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
          <div className="history-mailer-preview">
            <div className="history-mailer-stat">
              <span>Month</span>
              <strong>{selectedMonth ? `${selectedMonth.monthLabel} ${selectedMonth.year}` : '-'}</strong>
            </div>
            <div className="history-mailer-stat">
              <span>Average R</span>
              <strong>{monthlyEntries.length ? monthlyAverage : '-'}</strong>
            </div>
            <div className="history-mailer-stat">
              <span>Weakest Variable</span>
              <strong>{monthlyWeakest}</strong>
            </div>
            <div className="history-mailer-stat">
              <span>Best Day</span>
              <strong>{monthlyBest ? `${monthlyBest.date} - ${monthlyBest.r_score}` : '-'}</strong>
            </div>
          </div>
          <div className="history-mailer-actions">
            <button className="btn bg-pri" onClick={handleDownloadReport}>
              Download PDF
            </button>
            <button className="btn bg-ghost" onClick={() => setReportOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default HistoryPanel
